<template>
  <div class="video-player">
    <div class="videowrapper">
      <video v-lazy-load 
        ref="videoElement"
        class="video"
        :autoplay="autoplay"
        :loop="loop"
        :muted="muted"
        :data-poster="posterUrl || undefined"
        playsinline
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

type UploadVideoField = {
  __typename?: 'UploadVideoField' | string | null;
  muxPlaybackId?: string | null;
  streamingUrl?: string | null;
  mp4Med?: string | null;
};

const props = withDefaults(
  defineProps<{
    data: UploadVideoField;
    posterUrl?: string;
    autoplay?: boolean;
    loop?: boolean;
    muted?: boolean;
  }>(),
  {
    posterUrl: '',
    autoplay: true,
    loop: true,
    muted: true
  }
);

const autoplay = props.autoplay;
const loop = props.loop;
const muted = props.muted;
const posterUrl = props.posterUrl;

const videoElement = ref<HTMLVideoElement | null>(null);
let visibilityObserver: IntersectionObserver | null = null;

const tryAutoplay = async () => {
  if (!autoplay || !videoElement.value) {
    return;
  }

  try {
    await videoElement.value.play();
  } catch {
    // Ignore autoplay failures caused by browser/user settings.
  }
};

onMounted(() => {
  const streamUrl = props.data.streamingUrl;
  const mp4Url = props.data.mp4Med;
  const video = videoElement.value;

  if (!video) {
    return;
  }

  // Progressive MP4 is the primary path: these are short, muted, looping
  // background clips, so HLS buys us no adaptive benefit and Chromium stalls
  // on the Mux playlist (both natively and through MSE).
  if (mp4Url) {
    video.src = mp4Url;
  } else if (streamUrl && video.canPlayType('application/vnd.apple.mpegurl')) {
    // Fallback for assets whose MP4 rendition is not ready yet (still encoding).
    video.src = streamUrl;
  } else {
    return;
  }

  if (!autoplay) {
    return;
  }

  video.addEventListener('loadedmetadata', tryAutoplay, { once: true });

  // The player can sit inside a hidden overlay (the menu uses v-show), and
  // browsers refuse to start playback for an element that is not rendered.
  // Retry once it becomes visible. Play-only on purpose: never pause here, so
  // a missing or misbehaving observer can only fail to help, never stall us.
  visibilityObserver = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        tryAutoplay();
      }
    },
    { threshold: 0.1 }
  );

  visibilityObserver.observe(video);
});

onBeforeUnmount(() => {
  visibilityObserver?.disconnect();
  visibilityObserver = null;
});

</script>

<style scoped lang="scss">
.video-player,
.videowrapper,
.video {
  width: 100%;
  height: 100%;
}

.video {
  display: block;
  object-fit: cover;
}
</style>
